import { useEffect, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import hrIdle from 'assets/lottie/hr-idle.json';
import { relative } from 'path';

interface HrAvatarProps {
  /** Текст вопроса — печатается по буквам, как будто ассистент его генерирует */
  text: string;
  /** Режим «думает»: вместо вопроса ассистент показывает фразы ожидания с анимацией */
  thinking?: boolean;
  /** Фразы для режима «думает» (перебираются по кругу). Если не заданы — используются дефолтные. */
  thinkingMessages?: string[];
}

const DEFAULT_THINKING_MESSAGES = [
  'Думаю над следующим вопросом…',
  'Обрабатываю ваш ответ…',
  'Формулирую вопрос…',
  'Секунду, собираю мысли…'
];

/**
 * Посимвольно «печатает» текст — создаёт ощущение, что виртуальный HR
 * генерирует вопрос в реальном времени. При смене текста печать начинается заново.
 */
const useTypewriter = (text: string, speed = 14) => {
  const [shown, setShown] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setShown('');
    setDone(false);
    if (!text) {
      setDone(true);
      return;
    }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed]);

  return { shown, done };
};

/** Три «прыгающие» точки — индикатор того, что ассистент печатает/думает. */
const ThinkingDots = () => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', ml: '4px', gap: '3px' }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        component="span"
        sx={{
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          bgcolor: 'primary.main',
          animation: 'hrDotBounce 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.18}s`,
          '@keyframes hrDotBounce': {
            '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '40%': { transform: 'translateY(-4px)', opacity: 1 }
          }
        }}
      />
    ))}
  </Box>
);

/**
 * Виртуальный HR-ассистент: Lottie-анимация + «облачко» с текстом вопроса.
 * Текст печатается по буквам, чтобы выглядело так, будто ассистент его генерирует.
 * В режиме `thinking` показывает перебирающиеся фразы ожидания с анимированными точками.
 */
const HrAvatar = ({ text, thinking = false, thinkingMessages }: HrAvatarProps) => {
  const phrases = thinkingMessages?.length ? thinkingMessages : DEFAULT_THINKING_MESSAGES;
  const [phraseIdx, setPhraseIdx] = useState(0);

  // В режиме «думает» перебираем фразы по кругу, пока идёт загрузка
  useEffect(() => {
    if (!thinking) {
      setPhraseIdx(0);
      return;
    }
    const id = setInterval(() => setPhraseIdx((i) => (i + 1) % phrases.length), 2800);
    return () => clearInterval(id);
  }, [thinking, phrases.length]);

  const activeText = thinking ? phrases[phraseIdx] : text;
  const { shown, done } = useTypewriter(activeText);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 470 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* «Облачко» с вопросом — хвостик указывает вниз, на аватар */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          width: '100%',
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[2],
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 14,
            height: 14,
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            transform: 'rotate(45deg)',
            bottom: -8,
            left: '50%',
            ml: '-7px'
          }
        })}
      >
        <Typography
          variant="subtitle1"
          sx={{
            position: 'relative',
            fontSize: {
              xs: 'clamp(14px, 4.5vw, 16px)',
              sm: 'clamp(15px, 2.8vw, 18px)',
              md: 'clamp(16px, 2.2vw, 20px)'
            },
            lineHeight: 1.45
          }}
        >
          {/* Невидимая копия ПОЛНОГО текста — резервирует высоту облака заранее,
              чтобы оно не «росло» и не сдвигало аватар по мере печати. */}
          <Box component="span" aria-hidden sx={{ visibility: 'hidden', display: 'block' }}>
            {activeText || ' '}
          </Box>
          {/* Печатаемый текст поверх — абсолютом, на высоту блока уже не влияет. */}
          <Box component="span" sx={{ position: 'absolute', inset: 0, display: 'block' }}>
            {shown}
            {/* В режиме «думает» — анимированные точки (пока идёт загрузка);
                в обычном режиме — мигающий курсор, пока печатается вопрос. */}
            {thinking ? (
              <ThinkingDots />
            ) : (
              !done && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '1.1em',
                    ml: '2px',
                    verticalAlign: 'text-bottom',
                    bgcolor: 'primary.main',
                    animation: 'hrCaretBlink 0.9s steps(1) infinite',
                    '@keyframes hrCaretBlink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0 }
                    }
                  }}
                />
              )
            )}
          </Box>
        </Typography>
      </Paper>

      {/* Крупный аватар — занимает место бывшего превью камеры */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 450 },
          aspectRatio: { xs: '1 / 1', sm: '16 / 9' },
          maxHeight: { xs: 'calc(var(--vh, 1vh) * 30)', sm: 'calc(var(--vh, 1vh) * 50)' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
          // position: 'absolute',
          // transform: 'translate(-50%, -50%)',
          // top: {xs: 'calc(50% - 60px)', md: 'calc(50% - 1px)'},
          // left: '50%'
        }}
      >
        {/*
          Холст Lottie — 1000×1000, но полезное содержимое (ромб с волной) занимает лишь
          центральную область ~283–717. Через rendererSettings.viewBoxSize кадрируем SVG по
          содержимому, убирая большие пустые поля сверху/снизу — так высота блока становится
          предсказуемой и не нужен отрицательный top.
        */}
        <Lottie
          animationData={hrIdle}
          loop
          autoplay
          rendererSettings={{ viewBoxSize: '260 260 480 480', preserveAspectRatio: 'xMidYMid meet' }}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
    </Box>
  );
};

export default HrAvatar;
